/**
 * Agent Registry API Service - APB-1964
 *
 * HTTP client for /api/v1/agent-registry/agents endpoints.
 * Follows the shadowAgentApi.ts pattern:
 * - Uses fetch() with getFullApiUrl() and getAuthHeadersSync()
 * - Type-safe wrapper for all CRUD and governance operations
 */

import { getAuthHeadersSync } from '@shared/utils/authHelpers';
import { getFullApiUrl } from '../../../shared/utils/apiConfig.js';

import type {
  AgentRegistryEntry,
  AgentRegistryCreateRequest,
  AgentRegistryUpdateRequest,
  AgentRegistryListResponse,
  AgentRegistryFilters,
} from '../types/index.js';

const BASE_PATH = '/v1/agent-registry/agents';

/** Default request timeout in milliseconds (30 seconds) */
const DEFAULT_TIMEOUT_MS = 30_000;

function getUrl(path: string = ''): string {
  return `${getFullApiUrl()}${BASE_PATH}${path}`;
}

async function apiFetch<T>(
  path: string,
  options: RequestInit & { timeoutMs?: number } = {},
): Promise<T> {
  const { timeoutMs = DEFAULT_TIMEOUT_MS, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeadersSync(),
    ...((fetchOptions.headers as Record<string, string>) || {}),
  };

  // AbortController for request timeout + cancellation support
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(getUrl(path), {
      ...fetchOptions,
      headers,
      credentials: 'include',
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorBody = await response.text().catch(() => '');
      throw new Error(
        `Agent Registry API error ${response.status}: ${errorBody}`,
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error(`Agent Registry API request timed out after ${timeoutMs}ms: ${path}`);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ---------------------------------------------------------------------------
// Agent Registry endpoints
// ---------------------------------------------------------------------------

/**
 * List all registered agents with optional filters
 */
export async function listAgents(
  filters: AgentRegistryFilters,
): Promise<AgentRegistryListResponse> {
  const params = new URLSearchParams();

  if (filters.runtime_type) params.set('runtime_type', filters.runtime_type);
  if (filters.autonomy_level) params.set('autonomy_level', filters.autonomy_level);
  if (filters.ai_risk_level) params.set('ai_risk_level', filters.ai_risk_level);
  if (filters.is_approved !== undefined) {
    params.set('is_approved', String(filters.is_approved));
  }
  if (filters.is_active !== undefined) {
    params.set('is_active', String(filters.is_active));
  }
  params.set('limit', String(filters.limit ?? 50));
  params.set('offset', String(filters.offset ?? 0));

  const query = params.toString();
  const path = query ? `?${query}` : '';

  return apiFetch<AgentRegistryListResponse>(path);
}

/**
 * Get a single agent by ID
 */
export async function getAgent(agentId: string): Promise<AgentRegistryEntry> {
  return apiFetch<AgentRegistryEntry>(`/${agentId}`);
}

/**
 * Create a new agent registration
 */
export async function createAgent(
  request: AgentRegistryCreateRequest,
): Promise<AgentRegistryEntry> {
  return apiFetch<AgentRegistryEntry>('', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

/**
 * Update an existing agent registration
 */
export async function updateAgent(
  agentId: string,
  request: AgentRegistryUpdateRequest,
): Promise<AgentRegistryEntry> {
  return apiFetch<AgentRegistryEntry>(`/${agentId}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

/**
 * Delete an agent registration
 */
export async function deleteAgent(agentId: string): Promise<void> {
  await apiFetch<void>(`/${agentId}`, {
    method: 'DELETE',
  });
}

/**
 * Approve an agent for deployment
 * NOTE: This is a POST endpoint, not PUT
 */
export async function approveAgent(agentId: string): Promise<AgentRegistryEntry> {
  return apiFetch<AgentRegistryEntry>(`/${agentId}/approve`, {
    method: 'POST',
  });
}

/**
 * Lookup an agent by runtime ID
 * Returns the agent if it exists
 */
export async function lookupByRuntimeId(
  runtimeId: string,
): Promise<AgentRegistryEntry | null> {
  try {
    return await apiFetch<AgentRegistryEntry>(`/lookup/${runtimeId}`);
  } catch (error) {
    // Return null if not found (404)
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
}
